/**
 * Mantenimiento Cobros x Mayor — migración web de ares9740.
 *
 * Lista los recibos de cobro de ventas al por mayor por rango de fechas y
 * permite:
 *   - Anular un recibo vigente: revierte lo aplicado a las cuotas y reabre
 *     las facturas afectadas.
 *   - Reimprimir el recibo de cobro con su detalle por factura/cuota.
 */
import { encabezadoEmpresaHtml } from "lib/impresionhtml";
import { monedaLabel, etiquetaCuota } from "modules/ventasxmayor/impresionxmayor";
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin cobros en el rango
      </Typography>
    </Stack>
  );
}

// Recibo de cobro x mayor (impresión con iframe oculto).
function imprimirRecibo(det) {
  const r = det.recibo || {};
  const compania = det.compania || {};
  const detalle = det.detalle || [];
  const filas = detalle
    .map(
      (d) =>
        `<tr>
          <td style="padding:3px 6px;border-bottom:1px solid #ddd">${d.nfact || ""}</td>
          <td style="padding:3px 6px;border-bottom:1px solid #ddd">${etiquetaCuota(
            d.cuotacompromiso
          )}</td>
          <td style="padding:3px 6px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
            d.valorpagado
          )}</td>
        </tr>`
    )
    .join("");
  const html = `
    <html><head><meta name="color-scheme" content="light"><title>Recibo de Cobro Ventas x Mayor</title></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:18px;font-size:13px;max-width:480px;margin:0 auto">
      ${encabezadoEmpresaHtml(compania)}
      <p style="text-align:center;margin:0 0 14px;font-weight:bold">Recibo de Cobro Ventas x Mayor</p>
      <table style="width:100%;border-collapse:collapse;margin-bottom:14px">
        <tr><td style="padding:3px 8px;color:#555"># Recibo</td><td style="padding:3px 8px;font-weight:bold">${
          r.id || ""
        }</td></tr>
        <tr><td style="padding:3px 8px;color:#555">Fecha</td><td style="padding:3px 8px;font-weight:bold">${
          r.fecha || ""
        }</td></tr>
        <tr><td style="padding:3px 8px;color:#555">Cliente</td><td style="padding:3px 8px;font-weight:bold">${
          r.cliente || ""
        }</td></tr>
      </table>
      <table style="width:100%;border-collapse:collapse;margin-bottom:8px">
        <tr>
          <th style="padding:3px 6px;text-align:left;border-bottom:1px solid #000">Factura</th>
          <th style="padding:3px 6px;text-align:left;border-bottom:1px solid #000">Cuota</th>
          <th style="padding:3px 6px;text-align:right;border-bottom:1px solid #000">Valor (${monedaLabel(
            r.moneda
          )})</th>
        </tr>
        ${filas}
      </table>
      <p style="text-align:right;font-weight:bold;margin:6px 0 2px">Total: ${monedaLabel(
        r.moneda
      )} ${fmtMoney(r.valorpagado)}</p>
      ${
        r.equivalente
          ? `<p style="text-align:right;margin:0 0 14px">Equivalente: ${monedaLabel(
              r.equivalentemoneda
            )} ${fmtMoney(r.equivalente)} (tasa ${fmtMoney(r.tasa)})</p>`
          : ""
      }
      <table style="width:100%;border-collapse:collapse;margin-top:10px">
        ${
          r.banco
            ? `<tr><td style="padding:3px 8px;color:#555">Forma de pago</td><td style="padding:3px 8px;font-weight:bold">${r.banco}</td></tr>`
            : ""
        }
        ${
          r.referencia
            ? `<tr><td style="padding:3px 8px;color:#555">Referencia</td><td style="padding:3px 8px;font-weight:bold">${r.referencia}</td></tr>`
            : ""
        }
        ${
          r.descripcion
            ? `<tr><td style="padding:3px 8px;color:#555">Descripción</td><td style="padding:3px 8px;font-weight:bold">${r.descripcion}</td></tr>`
            : ""
        }
      </table>
      <p style="margin-top:34px;border-top:1px solid #000;padding-top:6px;text-align:center">Preparado Por</p>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

export default function MantenimientoCobrosXmayor({ embedded = false, refreshToken = 0 } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/xmayor_cobros_listar", {
        fechadesde: fecha1,
        fechahasta: fecha2,
      });
      if (ok) {
        setRows((data.cobros || []).map((r) => ({ ...r, id: r.id })));
      } else {
        showAlert({ message: data.Error || "No se pudieron listar los cobros", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  // ---- Anulación ----
  const [target, setTarget] = useState(null);
  const [anulando, setAnulando] = useState(false);

  const anular = async () => {
    setAnulando(true);
    try {
      const { ok, data } = await post("/api/xmayor_cobro_anular", { recibo: target.id });
      if (ok) {
        showAlert({ message: data.mensajeok || "Cobro anulado", color: "success" });
        setTarget(null);
        cargar();
      } else {
        showAlert({ message: data.Error || "No se pudo anular el cobro", color: "error" });
      }
    } finally {
      setAnulando(false);
    }
  };

  // ---- Reimpresión ----
  const [imprimiendo, setImprimiendo] = useState(0);

  const reimprimir = async (row) => {
    setImprimiendo(row.id);
    try {
      const { ok, data } = await post("/api/xmayor_recibo", { recibo: row.id });
      if (ok) {
        imprimirRecibo(data);
      } else {
        showAlert({ message: data.Error || "No se pudo cargar el recibo", color: "error" });
      }
    } finally {
      setImprimiendo(0);
    }
  };

  const columns = [
    { field: "id", headerName: "Recibo#", width: 90 },
    { field: "fecha", headerName: "Fecha", width: 110 },
    { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 180 },
    { field: "facturas", headerName: "Facturas", width: 140 },
    { field: "banco", headerName: "Forma", width: 130 },
    {
      field: "valorpagado",
      headerName: "Valor",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "estado",
      headerName: "Estado",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        params.row.anulado ? (
          <Chip
            size="small"
            label="Anulado"
            sx={{ bgcolor: "#FFEBEE", color: "#C62828", fontWeight: 700 }}
          />
        ) : (
          <Chip
            size="small"
            label="Vigente"
            sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
          />
        ),
    },
    {
      field: "acciones",
      headerName: "",
      width: 240,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.8}>
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setTarget(params.row)}
            disabled={!!params.row.anulado}
            sx={btnOutline(FP.color.danger)}
          >
            Anular
          </Button>
          <Button
            size="small"
            startIcon={
              imprimiendo === params.row.id ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <PrintOutlinedIcon />
              )
            }
            onClick={() => reimprimir(params.row)}
            disabled={imprimiendo === params.row.id}
            sx={btnOutline(FP.color.navy)}
          >
            Reimprimir
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box
      component={embedded ? "section" : "main"}
      sx={embedded ? {} : { minHeight: "100vh", bgcolor: FP.color.surface }}
    >
      {!embedded && (
        <PageHeader
          title="Mantenimiento Cobros x Mayor"
          subtitle="Consulta, anula y reimprime los recibos de cobro de ventas al por mayor"
        />
      )}
      <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={cargar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
            >
              <Typography
                sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.85rem" }}
              >
                {rows.length} cobros
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              rowHeight={46}
              pageSizeOptions={[25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>

      {/* Confirmación de anulación */}
      <Dialog
        open={!!target}
        onClose={() => setTarget(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.danger }}>Anular cobro</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem" }}>
            Recibo <b>{target?.id}</b> de <b>{target?.cliente}</b> por RD${" "}
            <b>{fmtMoney(target?.valorpagado)}</b>. La anulación revierte lo aplicado a las cuotas y
            reabre las facturas afectadas. ¿Desea continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTarget(null)} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              anulando ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />
            }
            onClick={anular}
            disabled={anulando}
            sx={btnSolid("#D32F2F", "#B71C1C")}
          >
            Sí, anular
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
